import React, { useCallback, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import searchAgentAdapter from "../../adapters/searchAgentAdapter";
import StudentDetailsLayout from "../../components/StudentDetails/StudentDetailsLayout";
import StudentInfoCard from "../../components/StudentDetails/cards/StudentInfoCard";
import OtpPasswordManagementCard from "../../components/StudentDetails/cards/OtpPasswordManagementCard";
import { maskPhoneNumber } from "../../components/StudentDetails/utils/studentDetailsHelpers";
import ErrorSnackbar from "../../widgets/Snackbar/ErrorSnackbar";
import Loader from "../../components/Loader/Loader";
import routes from "../../constants/routes";
import { PRODUCT_ID } from "../../constants/constants";
import StudentNotFoundState from "../Student/components/StudentNotFoundState";
import useAgentDetailsQuery from "./hooks/useAgentDetailsQuery";
import useAgentRouteContext from "./hooks/useAgentRouteContext";

const AgentDetailsContainer = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const { stateAgentData, statePhone, decryptedPhone } =
    useAgentRouteContext(location);

  const {
    data: queryResult,
    isLoading,
    error,
    shouldFetch,
  } = useAgentDetailsQuery({
    stateAgentData,
    decryptedPhone,
  });

  const localAgentData = useMemo(() => {
    if (stateAgentData) {
      return stateAgentData;
    }
    
    if (!queryResult) {
      return null;
    }
    if(!queryResult.agents) {
      return null;
    }

    return searchAgentAdapter(queryResult.agents[0]);
  }, [stateAgentData, queryResult]);

  const localPhone = statePhone || localAgentData?.phone || decryptedPhone;

  const handleBack = useCallback(() => {
    navigate(routes.manageAgent.root);
  }, [navigate]);

  const errorMessage =
    (error && error.message) ||
    (!isLoading && !localAgentData && shouldFetch
      ? "No agent found with the provided phone number."
      : "") ||
    (!decryptedPhone && !stateAgentData
      ? "Phone number is missing in URL."
      : "");

  if (isLoading) return <Loader fullScreen />;

  if (!localAgentData) {
    return (
      <StudentNotFoundState
        errorMessage={errorMessage}
        onBack={handleBack}
        message="No agent data found."
      />
    );
  }

  const {
    first_name: firstName,
    last_name: lastName,
    uam_id: uamId,
    phone: responsePhone,
    admission_number: admissionNumber,
    crn_id: crnId,
  } = localAgentData;

  const agentSummaryFields = [
    { label: "First Name", value: firstName || "-" },
    { label: "Last Name", value: lastName || "-" },
    { label: "UAM ID", value: uamId || "-" },
    { label: "Admission Number", value: admissionNumber || "-" },
    {
      label: "Masked Phone",
      value: maskPhoneNumber(responsePhone || localPhone) || "-",
    },
    { label: "CRN ID", value: crnId || "-" },
  ];

  const operationGroups = [
    {
      title: "Reset OTP",
      content: (
        <OtpPasswordManagementCard
          phone={localPhone}
          features={{ resetOtp: true }}
          studentData={localAgentData}
          verticalInfo={{ productId: PRODUCT_ID }}
        />
      ),
    },
  ];

  return (
    <>
      <StudentDetailsLayout
        title="Agent Details"
        backRoute={routes.manageAgent.root}
        operationsTitle="Agent Operations"
        noDataMessage="No agent data found."
        studentData={localAgentData}
        studentInfoSlot={
          <StudentInfoCard
            studentData={localAgentData}
            phone={localPhone}
            fallbackInitials="AG"
            summaryFields={agentSummaryFields}
          />
        }
        operationGroups={operationGroups}
      />

      <ErrorSnackbar
        open={!!errorMessage}
        message={errorMessage}
        onClose={() => {}}
      />
    </>
  );
};

export default AgentDetailsContainer;
